import { useState } from 'react';
import { createPortal } from 'react-dom';

import { 
	FreeBetVoucherConditionInfo,
	Labels,
	RewardType,
	VoucherInfo, 
	VoucherType as VoucherTypeEnum,
} from '@/types';
import { addCommas, replaceResource } from '@/utils';
import { extractDateParts, formatExpireDateWithTime } from '@/utils/date';
import { voucherService } from '../../services/voucher.service';

import { useReward } from '@/contexts/reward-context';
import { ExpireIcon, InfoIcon } from '@/components/svg-icons';
import { EventVoucherIcon } from '@/components/icons/event-voucher-icon';
import { EventButton } from '@/components/buttons/event-button';
import { VoucherDetailModal } from '../voucher-detail-modal';

const formatExpireDate = (expireDate: string) => {
	const { year, month, day } = extractDateParts(expireDate);
	return `${year}/${month}/${day}(GMT-4)`;
};

const createVoucherContentMap = (labels?: Labels, currencyName?: string) => ({
	[VoucherTypeEnum.OddsBooster]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		kafka: {
			item: 21,
			button: 23
		},
		getDescription: () => '',
	},
	[VoucherTypeEnum.RiskFreeBet]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		kafka: {
			item: 22,
			button: 24
		},
		getDescription: (quota: number) => 
			replaceResource(labels?.lbl_RPMaxSubsidy ?? '', addCommas(quota)),
	},
	[VoucherTypeEnum.CashExchange]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		getDescription: () => '',
	},
	[VoucherTypeEnum.NicknameChange]: {
		isGift: false,
		showTag: false,
		isShowCount: true,
		showExpireDate: false,
		hasDetail: false,
		getDescription: () => labels?.lbl_nicknamechg1,
	},
	[VoucherTypeEnum.FreeOddsBooster]: {
		isGift: true,
		showTag: true,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: true,
		kafka: {
			item: 8,
			button: 9
		},
		getDescription: () => '',
	},
	[VoucherTypeEnum.BetterCashExchange]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		getDescription: () => '',
	},
	[VoucherTypeEnum.BestCashExchange]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		getDescription: () => '',
	},
	[VoucherTypeEnum.NewRiskFreeBet]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		kafka: {
			item: 22,
			button: 24
		},
		getDescription: (quota: number) => 
			replaceResource(labels?.lbl_RPMaxSubsidy ?? '', addCommas(quota)),
	},
	[VoucherTypeEnum.AffPromotionOddsBoost]: {
		isGift: true,
		showTag: true,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: true,
		// kafka: { 需要確認
		// 	item: 8,
		// 	button: 9
		// },
		getDescription: () => '',
	},
	[VoucherTypeEnum.StandardCashExchange]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		getDescription: () => '',
	},
	[VoucherTypeEnum.GoodCashExchange]: {
		isGift: false,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: false,
		getDescription: () => '',
	},
	[VoucherTypeEnum.GiftRiskFreeBet]: {
		isGift: true,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: true,
		kafka: {
			item: 4,
			button: 5
		},
		getDescription: (quota: number) => 
			replaceResource(labels?.lbl_RPMaxSubsidy ?? '', addCommas(quota)),
	},
	[VoucherTypeEnum.FreeBet]: {
		isGift: true,
		showTag: false,
		isShowCount: false,
		showExpireDate: true,
		hasDetail: true,
		kafka: {
			item: 1,
			button: 2,
		},
		getDescription: (quota: number) =>
			(labels?.lbl_FBInfo01 ?? '')
				.replace(/\{Amount\}/g, addCommas(quota))
				.replace(/\{Currency\}/g, currencyName || ''),
	},
} as const);

const getVoucherContent = (
	voucher: VoucherInfo,
	labels?: Labels,
	currencyName?: string
) => {
	const voucherContentMap = createVoucherContentMap(labels, currencyName);
	const voucherContent = voucherContentMap[voucher.VoucherType];

	if (!voucherContent) {
		return {
			title: '', 
			isGift: false,
			isShowCount: false,
			isExpiringSoon: false,
			showExpireDate: false,
			hasDetail: false,
			description: '',
		};
	}

	return {
		title: voucherService.getVoucherTitle(voucher.VoucherType, labels),
		isGift: voucherContent.isGift,
		showTag: voucherContent.showTag,
		isShowCount: voucherContent.isShowCount,
		isExpiringSoon: voucherService.checkVoucherStatus(voucher.ExpireDate).isExpiringSoon,
		showExpireDate: voucherContent.showExpireDate,
		exprieDate: voucherContent.showExpireDate 
			? formatExpireDateWithTime(voucher.ExpireDate)
			: undefined,
		hasDetail: voucherContent.hasDetail,
		kafka: voucherContent.kafka,
		description: voucherContent.getDescription(voucher.Quota)
	};
};

export const VoucherItem = ({ 
	voucher,
	count
}: {
	voucher: VoucherInfo,
	count: number;
}) => {
	const [isShowModal, setIsShowModal] = useState(false);
	const [voucherConditionInfo, setVoucherConditionInfo] = useState<FreeBetVoucherConditionInfo | null>(null);
	const {
		labels,
		currencyName,
		goToEvent,
		onGoToWorldCompetition,
		onGoToLeague,
		onOpenRewardNickNameModal, 
		onKafkaSet,
		onExclusiveKafkaSet,
		getVoucherConditionInfo,
		onClose
	} = useReward();
	const voucherContent = getVoucherContent(voucher, labels, currencyName);
	const isFreeOddsBoosterVoucher = voucher.VoucherType === VoucherTypeEnum.FreeOddsBooster;
	const isAffOddsBoosterrVoucher = voucher.VoucherType === VoucherTypeEnum.AffPromotionOddsBoost;
	const isGiftRiskFreeBetVoucher = voucher.VoucherType === VoucherTypeEnum.GiftRiskFreeBet;
	const isFreeBetVoucher = voucher.VoucherType === VoucherTypeEnum.FreeBet;
	const isNotYetEffective = voucherService.isNotYetEffective(voucher);
	const effectiveDate = isNotYetEffective && voucher.EffectiveDate
		? formatExpireDateWithTime(voucher.EffectiveDate).replace(/\s*\(GMT-4\)$/, '')
		: '';
	const isExpiringSoon = !isNotYetEffective && voucherContent.isExpiringSoon;
	const effectiveDateText = labels?.lbl_FBInfo04
		? labels.lbl_FBInfo04
			.replace(/\{Effective_Date\}/g, effectiveDate)
			.replace(/\{Voucher_Timezone\}/g, 'GMT-4')
		: `${effectiveDate} (GMT-4)`;

	const sendKafka = ({ fromButton }: { fromButton: boolean }) => {
		if (voucherContent.kafka) {
			const kafkaFn = isGiftRiskFreeBetVoucher ? onExclusiveKafkaSet : onKafkaSet;
			const kafkaType = {
				[VoucherTypeEnum.FreeOddsBooster]: 9,
				[VoucherTypeEnum.GiftRiskFreeBet]: 1,
				[VoucherTypeEnum.FreeBet]: 10,
			}[voucher.VoucherType] ?? 3;

			kafkaFn({
				type: kafkaType,
				action: fromButton 
					? voucherContent.kafka.button
					: voucherContent.kafka.item
			});
		}
	};

	const goToUse = () => {
		if (voucher.VoucherType === VoucherTypeEnum.NicknameChange) {
			onClose();
			onOpenRewardNickNameModal?.();
			return;
		}

		// navigate to EPL league
		if (isFreeOddsBoosterVoucher) {
			onClose();
			onGoToLeague?.({ sport: 1, leagueId: [3] });
			onKafkaSet({
				type: 9,
				action: 10
			});
			return;
		}

		if (isAffOddsBoosterrVoucher) {
			onClose();
			onGoToLeague?.({ sport: 1, leagueId: [3, 4, 5, 8, 43] });
			// TODO 
			// onKafkaSet({
			// 	type: ,
			// 	action: 
			// });
			return;
		}

		if (isGiftRiskFreeBetVoucher) {
			onClose();
			onGoToWorldCompetition?.(34);
			return;
		}

		goToEvent?.(RewardType.Betting, 1, false);
	};

	const handleUseButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();

		if (isNotYetEffective) return;

		goToUse();
		sendKafka({ fromButton: true });
	};

	const handleVoucherItemClick = async () => {
		if (!voucherContent.hasDetail) {
			if (isNotYetEffective) return;

			goToUse();
			sendKafka({ fromButton: false });
			return;
		}

		if (isFreeBetVoucher && getVoucherConditionInfo) {
			try {
				const conditionInfo = await getVoucherConditionInfo(voucher.VoucherDetailId, voucher.VoucherSystemVersion);
				setVoucherConditionInfo(conditionInfo);
			} catch (error) {
				// TODO: handle error
			}
		}

		setIsShowModal(true);
		sendKafka({ fromButton: false });
	};

	const closeVoucherDetailModal = () => {
		setIsShowModal(false);
		setVoucherConditionInfo(null);
	};

	return (
		<div 
			className="shop-voucher shop-voucher--my-coupon"
			data-state={isExpiringSoon}
			data-not-effective={isNotYetEffective}
			onClick={handleVoucherItemClick}
		>
			<div className="shop-voucher-tag">
				{isExpiringSoon ? (
					<div className="shop-voucher-tag__text shop-voucher-tag__text--expiring">
						{labels?.lbl_RPExpireSoon}
					</div>
				) : null}
			</div>
			<div className="shop-voucher-pack">
				<EventVoucherIcon 
					voucherType={voucher.VoucherType}
					isGift={voucherContent.isGift}
					promotionInfo={voucher.PromotionInfo}
				/>
			</div>
			<div className="shop-voucher-info-content">
				<div className="shop-voucher-info">
					<div className="shop-voucher-info__title">
						{voucherContent.title}
					</div>

					{voucherContent.showTag ? (
						<div className="shop-voucher-info__tag">{labels?.lbl_giveaway}</div>
					) : null}

					<div className="shop-voucher-info__desc">
						{voucherContent.description}
					</div>
					
					{isNotYetEffective ? (
						<div className="shop-voucher-info__effective-date">
							{effectiveDateText}
						</div>
					) : voucherContent.showExpireDate ? (
						<div className="shop-voucher-info__expire-date">
							<ExpireIcon className="shop-voucher-info-expire-date-icon" />
							{voucherContent.exprieDate}
						</div>
					) : null}

					{voucherContent.isShowCount ? (
						<div className="shop-voucher-info__quantity">
							{`${labels?.lbl_numowned} : ${count}`} 
						</div>
					) : null}

					{voucherContent.hasDetail ? (
						<div className="shop-voucher-info__intro">
							{labels?.lbl_IDRRFVoucherRules03}
							<InfoIcon />
						</div>
					) : null}
				</div>

				{isNotYetEffective ? null : (
					<EventButton onClick={handleUseButtonClick}>
						{labels?.lbl_RPGoToUse}
					</EventButton>
				)}
			</div>

			{isShowModal ? createPortal(
				<VoucherDetailModal 
					voucher={voucher}
					voucherDetailInfo={{
						title: voucherContent.title,
						isGift: voucherContent.isGift,
					}}
					voucherConditionInfo={voucherConditionInfo}
					onClose={closeVoucherDetailModal} 
				/>, 
				document.getElementById('shop-wrapper')!
			) : null}
		</div>
	);
};